import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import VerificationCodeInput from './VerificationCodeInput';

const LeadFormStep2 = ({ email, firstName, verificationId, onVerified }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1800);
  const [isResending, setIsResending] = useState(false);
  const [currentVerificationId, setCurrentVerificationId] = useState(verificationId);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [currentVerificationId]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleCodeComplete = async (code) => {
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/verify/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_id: currentVerificationId, code })
      });
      
      const data = await response.json();
      
      if (data.success && data.verified) {
        toast.success('Email verified successfully!');
        onVerified(data.lead_id);
      } else {
        toast.error(data.error?.message || 'Verification failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Verification error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleResendCode = async () => {
    setIsResending(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/verify/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_id: currentVerificationId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || 'New code sent! Check your email.');
        setCurrentVerificationId(data.verification_id);
        setTimeLeft(1800);
      } else {
        toast.error(data.error?.message || 'Failed to resend code');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Resend error:', error);
    } finally {
      setIsResending(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-navy py-16 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="p-8 bg-ivory">
          <CardHeader>
            <CardTitle className="text-3xl font-playfair text-navy mb-2">
              Verify Your Email
            </CardTitle>
            <p className="text-gray-600">
              We've sent a 6-digit code to <strong>{email}</strong>
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Progress indicator */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-semibold">✓</div>
                <span className="ml-2 text-sm font-medium text-green-600">Basic Info</span>
              </div>
              <div className="flex-1 h-0.5 bg-green-500 mx-4" />
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-semibold">2</div>
                <span className="ml-2 text-sm font-medium">Verify Email</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-300 mx-4" />
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center">3</div>
                <span className="ml-2 text-sm text-gray-500">Details</span>
              </div>
            </div>
            
            <VerificationCodeInput
              onComplete={handleCodeComplete}
              isSubmitting={isSubmitting}
            />
            
            <p className="text-center text-sm text-gray-500">
              {timeLeft > 0 ? (
                <>Code expires in: <span className="font-medium text-navy">{formatTime(timeLeft)}</span></>
              ) : (
                <span className="text-red-500">Code expired. Please request a new code.</span>
              )}
            </p>
            
            <div className="text-center">
              <Button
                variant="ghost"
                onClick={handleResendCode}
                disabled={isResending || timeLeft > 1740}
                className="text-gold hover:text-gold-600"
                data-testid="resend-code-button"
              >
                {isResending ? 'Sending...' : 'Resend Code'}
              </Button>
            </div>
            
            <div className="pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center">
                Didn't receive the code? Check your spam folder or{' '}
                <a data-testid="lead-form-step2-a-1" href="tel:+18445993355" className="text-gold hover:underline">
                  call us at 1-844-599-3355
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LeadFormStep2;
